import type { Address } from '@payaca/types/locationTypes';
import type { Nullish } from '@payaca/utilities/types';
import { z } from 'zod/v4';

export type AddressCoordinates = { latitude: number; longitude: number };

export type StoredCoordinates = {
  latitude: Nullish<number>;
  longitude: Nullish<number>;
};

export type IndependentAddressCoordinates = {
  lookupLatitude: Nullish<number>;
  lookupLongitude: Nullish<number>;
  manualLatitude: Nullish<number>;
  manualLongitude: Nullish<number>;
};

/**
 * Coordinates a caller may store against an address. 0/0 is rejected because
 * {@link getAddressCoordinates} reads it back as no coordinates at all.
 */
export const settableAddressCoordinatesSchema = z
  .object({
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
  })
  .refine(({ latitude, longitude }) => latitude !== 0 || longitude !== 0, {
    message: 'A location of 0, 0 cannot be stored',
  });

/**
 * A geocode that returns nothing is persisted as 0/0 rather than null, so a
 * stored 0/0 means we have no coordinates, not a point in the Gulf of Guinea.
 * Only the pair counts as absent — 0 is a usable longitude in the UK.
 */
export const getAddressCoordinates = (
  coordinates: Nullish<StoredCoordinates>
): AddressCoordinates | null => {
  const { latitude, longitude } = coordinates ?? {};
  if (latitude == null || longitude == null) return null;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  if (latitude < -90 || latitude > 90) return null;
  if (longitude < -180 || longitude > 180) return null;
  if (latitude === 0 && longitude === 0) return null;
  return { latitude, longitude };
};

/**
 * Returns the complete usable pair for an address. Manual coordinates win as a
 * pair; the lookup pair is considered only when the manual pair is absent or
 * unusable. Keeping this decision here prevents callers from accidentally
 * combining one axis from each source.
 */
export const getEffectiveAddressCoordinates = (
  coordinates: IndependentAddressCoordinates
): AddressCoordinates | null => {
  return (
    getAddressCoordinates({
      latitude: coordinates.manualLatitude,
      longitude: coordinates.manualLongitude,
    }) ??
    getAddressCoordinates({
      latitude: coordinates.lookupLatitude,
      longitude: coordinates.lookupLongitude,
    })
  );
};

export type MapDestination = AddressCoordinates | { addressText: string };

export const getMapDestination = (
  coordinates: Nullish<StoredCoordinates>,
  addressText: Nullish<string>
): MapDestination | null => {
  const usableCoordinates = getAddressCoordinates(coordinates);
  if (usableCoordinates) return usableCoordinates;
  return addressText?.length ? { addressText } : null;
};

const asMapQuery = (destination: MapDestination) =>
  encodeURIComponent(
    'addressText' in destination
      ? destination.addressText
      : `${destination.latitude},${destination.longitude}`
  );

export const getGoogleMapsSearchUrl = (destination: MapDestination) =>
  `https://www.google.com/maps/search/?api=1&query=${asMapQuery(destination)}`;

export const getGoogleMapsDirectionsUrl = (destination: MapDestination) =>
  `https://www.google.com/maps/dir/?api=1&destination=${asMapQuery(
    destination
  )}`;

export const getAppleMapsDirectionsUrl = (destination: MapDestination) =>
  `https://maps.apple.com/?daddr=${asMapQuery(destination)}`;

export const getGoogleMapsSearchUrlForAddress = (
  coordinates: Nullish<StoredCoordinates>,
  addressText: Nullish<string>
): string | undefined => {
  const destination = getMapDestination(coordinates, addressText);
  return destination ? getGoogleMapsSearchUrl(destination) : undefined;
};

export const getAddressAsString = (
  address: Partial<
    Pick<
      Address,
      'line1' | 'line2' | 'city' | 'administrativeArea' | 'postcode'
    >
  >
) => {
  if (!address) return;

  const addressElements = [];
  addressElements.push(address.line1);
  addressElements.push(address.line2);
  addressElements.push(address.city);
  addressElements.push(address.administrativeArea);
  addressElements.push(address.postcode);

  return addressElements.filter(Boolean).join(', ');
};
